import { type FC, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Phone } from "lucide-react";
import { enqueueSnackbar } from "notistack";
import { TOAST } from "@/shared/constants/toastMessages";

import { Button } from "@/shared/components/ui/button";
import { PLAN_NAME_BY_TIER } from "@/shared/constants/planTiers";
import { supabase } from "@/shared/lib/supabase";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useAttorneyHelplineEnabled } from "@/shared/hooks/useAttorneyHelplineEnabled";
import { usePaymentTier } from "@/shared/hooks/usePaymentTier";
import { useUserCustomizations } from "@/shared/hooks/useUserCustomizations";
import { hasMinTier } from "@/shared/utils/tierOrder";

interface AttorneyCtaProps {
  title: string;
  body: string;
}

export const AttorneyCta: FC<AttorneyCtaProps> = ({ title, body }) => {
  const helplineEnabled = useAttorneyHelplineEnabled();
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const queryClient = useQueryClient();
  const { tier, attorneyHelplineMinTier, isLoading: isTierLoading } = usePaymentTier();
  const canRequestCall = hasMinTier(tier, attorneyHelplineMinTier);
  const { getCustomization } = useUserCustomizations();
  const attorneyCallRequestedAt = getCustomization<string>("attorney_call_requested_at", null);
  const alreadyRequested = !!attorneyCallRequestedAt;
  const [requesting, setRequesting] = useState(false);

  if (!helplineEnabled) return null;

  const handleRequest = async () => {
    if (requesting || alreadyRequested || !canRequestCall || !estateId) return;
    setRequesting(true);
    try {
      const { error } = await supabase.functions.invoke("communications/attorney-helpline", {
        body: { estate_id: estateId },
      });
      if (error) throw error;
      await queryClient.invalidateQueries({ queryKey: ["userData"] });
      enqueueSnackbar("Your call request has been sent. We'll be in touch within 1 business day.", {
        variant: "success",
      });
    } catch {
      enqueueSnackbar(TOAST.GENERIC_ERROR, { variant: "error" });
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 rounded-lg border border-brand bg-brand-subtle p-6">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Phone className="h-4 w-4 text-brand" />
          <span className="text-base font-semibold tracking-[-0.32px]">{title}</span>
        </div>
        <p className="text-sm text-secondary tracking-[-0.28px]">{body}</p>
      </div>
      {canRequestCall || alreadyRequested || isTierLoading ? (
        <Button
          variant="outline"
          rounded
          className="w-fit bg-white"
          disabled={requesting || alreadyRequested || isTierLoading}
          onClick={handleRequest}
        >
          {requesting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Requesting...
            </>
          ) : alreadyRequested ? (
            "Call requested"
          ) : (
            "Request a call"
          )}
        </Button>
      ) : (
        <p className="text-sm text-secondary">
          The attorney call is included with {PLAN_NAME_BY_TIER[attorneyHelplineMinTier]}.
        </p>
      )}
    </div>
  );
};
